import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Business as BriefcaseIcon,
  Code as CodeIcon,
  Language as GlobeIcon,
  Translate as LanguageIcon,
  Schedule as ClockIcon,
} from '@mui/icons-material';
import { useGlobalConfig } from 'context/GlobalContext';
import { PilotApis } from 'apis/pilotAPI';
import { Gauge } from 'lucide-react';
import { DatabaseZap } from 'lucide-react';
import { Users } from 'lucide-react';
import { useTheme } from '@mui/material/styles';
import { FileText } from 'lucide-react';
import { ServerCog } from 'lucide-react';


interface ProjectAttribute {
  icon: React.ReactNode;
  label: string;
  value: string;
}

interface ProjectAttributesSectionProps {
  projectId: string;
  utilityName: string;
  apiHost?: string; // API host for the pilot/utility
  country?: string;
  language?: string;
  ingestionType?: string;
  timezone?: string;
  customAttributes?: ProjectAttribute[];
  title?: string;
  subtitle?: string;
  description?: string;
}

export const ProjectAttributesSection: React.FC<ProjectAttributesSectionProps> = ({
  projectId,
  utilityName,
  apiHost,
  country: propCountry,
  language: propLanguage,
  ingestionType: propIngestionType,
  timezone: propTimezone,
  customAttributes,
  title = "Project Attributes",
  subtitle,
  description,
}) => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const [metadata, setMetadata] = useState<{ country?: string; timezone?: string; language?: string; fuelType?: string; meterType?: string; customerType?: string; ingestionType?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch project attributes from API
  useEffect(() => {
    const fetchProjectAttributes = async () => {
      if (!projectId) {
        setError('No pilot ID provided');
        return;
      }

      setIsLoading(true);
      setError(null);
      
      try {
        const response = await PilotApis.getProjectAttributes(rc, projectId);
        
        if (response.success && response.data) {
          // Extract project attributes from the response
          const { country, default_timezone, primary_language, allowed_measurement_type, supported_meter_types, default_user_segment,ingestion_type } = response.data?.attributes;
          
          setMetadata({
            country: country || '',
            timezone: default_timezone || '',
            language: primary_language || '',
            fuelType: allowed_measurement_type || '',
            meterType: supported_meter_types ? JSON.parse(supported_meter_types).join(', ') : '',
            customerType: default_user_segment || '',
            ingestionType: ingestion_type || '',
          });
          
          console.log('Project attributes fetched:', { country, default_timezone, primary_language, allowed_measurement_type, supported_meter_types, default_user_segment });
        } else {
          setError(response.error || 'Failed to fetch project attributes');
          console.error('Failed to fetch project attributes:', response.error);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error fetching project attributes:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectAttributes();
  }, []);

  // Use extracted metadata values, fall back to props, then to defaults
  const country = metadata.country || propCountry || "";
  const timezone = metadata.timezone || propTimezone || "";
  const language = metadata.language || propLanguage || "";
  const fuelType = metadata.fuelType || "";
  const meterType = metadata.meterType || "";
  const customerType = metadata.customerType || "";
  const ingestionType = metadata.ingestionType || propIngestionType || "";


  const defaultAttributes: ProjectAttribute[] = [
    {
      icon: <CodeIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Pilot ID',
      value: projectId,
    },
    {
      icon: <BriefcaseIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Project Name',
      value: utilityName,
    },
    {
      icon: <ServerCog style={{ color: `${theme.palette.text.secondary}`, opacity: 0.7 }} size={20} />,
      label: 'Ingestion Type',
      value: ingestionType,
    },
    {
      icon: <GlobeIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Country',
      value: country,
    },
    {
      icon: <LanguageIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Language',
      value: language,
    },
    {
      icon: <Users style={{ color: `${theme.palette.text.secondary}`, opacity: 0.7 }} size={20} />,
      label: 'Customer Type',
      value: customerType,
    },
    {
      icon: <DatabaseZap style={{ color: `${theme.palette.text.secondary}`, opacity: 0.7 }} size={20} />,
      label: 'Fuel Type',
      value: fuelType,
    },
    {
      icon: <Gauge style={{ color: `${theme.palette.text.secondary}`, opacity: 0.7 }} size={20} />,
      label: 'Meter Type',
      value: meterType,
    },
    {
      icon: <ClockIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Time Zone',
      value: timezone,
    },
  ];

  const attributes = customAttributes || defaultAttributes;
  const displaySubtitle = subtitle || `Core project information`;
  const defaultDescription = description;

  if (isLoading) {
    return (
      <Card sx={{ 
        bgcolor: 'background.paper', 
        boxShadow: 2, 
        borderRadius: 3,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderOpacity: 0.1
      }}>
        <CardContent sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // Always show basic project info (project ID and utility name) even on error
  const basicAttributes: ProjectAttribute[] = [
    {
      icon: <CodeIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Pilot ID',
      value: projectId,
    },
    {
      icon: <BriefcaseIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Utility Name',
      value: utilityName,
    },
  ];

  if (error) {
    return (
      <Card sx={{ 
        bgcolor: 'background.paper', 
        boxShadow: 2, 
        borderRadius: 3,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderOpacity: 0.1
      }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left', flex: 1, mb: 1 }}>
              <BriefcaseIcon sx={{ mr: 2.5, color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" component="h2" sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 0,
                fontSize: '1.125rem'
              }}>
                {title}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              lineHeight: 1.4,
              textAlign: 'left'
            }}>
              {displaySubtitle}
            </Typography>
          </Box>

          {/* Basic Attributes (always shown) */}
          <Box>
            {basicAttributes.map((attribute, index) => (
              <React.Fragment key={attribute.label}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  py: 2,
                  px: 0
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    {attribute.icon}
                    <Typography variant="body2" sx={{ 
                      ml: 2, 
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.875rem'
                    }}>
                      {attribute.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary',
                    fontWeight: 400,
                    fontSize: '0.875rem'
                  }}>
                    {attribute.value}
                  </Typography>
                </Box>
                {index < basicAttributes.length - 1 && (
                  <Divider sx={{ 
                    borderColor: 'divider',
                    opacity: 0.3,
                    mx: 0
                  }} />
                )}
              </React.Fragment>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ 
      bgcolor: 'background.paper', 
      boxShadow: 2, 
      borderRadius: 3,
      mb: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderOpacity: 0.1
    }}>
      <CardContent sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left', flex: 1, mb: 1 }}>
            <BriefcaseIcon sx={{ mr: 2.5, color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" component="h2" sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 0,
              fontSize: '1.125rem'
            }}>
              {title}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{
            color: 'text.secondary',
            fontSize: '0.875rem',
            lineHeight: 1.4,
            textAlign: 'left'
          }}>
            {displaySubtitle}
          </Typography>
        </Box>

        {/* Attributes List - Two Column Layout */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4 
        }}>
          {/* First Column - First 5 attributes */}
          <Box sx={{ flex: 1 }}>
            {attributes.slice(0, 5).map((attribute, index) => (
              <React.Fragment key={attribute.label}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  py: 2,
                  px: 0
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    {attribute.icon}
                    <Typography variant="body2" sx={{ 
                      ml: 2, 
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.875rem'
                    }}>
                      {attribute.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary',
                    fontWeight: 400,
                    fontSize: '0.875rem'
                  }}>
                    {attribute.value}
                  </Typography>
                </Box>
                {index < 4 && (
                  <Divider sx={{ 
                    borderColor: 'divider',
                    opacity: 0.3,
                    mx: 0
                  }} />
                )}
              </React.Fragment>
            ))}
          </Box>

          {/* Second Column - Remaining attributes */}
          {attributes.length > 6 && (
            <Box sx={{ flex: 1 }}>
              {attributes.slice(5).map((attribute, index) => (
                <React.Fragment key={attribute.label}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 2,
                    px: 0
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      {attribute.icon}
                      <Typography variant="body2" sx={{ 
                        ml: 2, 
                        color: 'text.primary',
                        fontWeight: 500,
                        fontSize: '0.875rem'
                      }}>
                        {attribute.label}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary',
                      fontWeight: 400,
                      fontSize: '0.875rem'
                    }}>
                      {attribute.value}
                    </Typography>
                  </Box>
                  {index < attributes.slice(5).length - 1 && (
                    <Divider sx={{ 
                      borderColor: 'divider',
                      opacity: 0.3,
                      mx: 0
                    }} />
                  )}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>

        {/* Project Description Section */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FileText style={{ color: `${theme.palette.text.secondary}`, opacity: 0.7, marginRight: 8 }} size={20} />
            <Typography variant="body2" sx={{ 
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '0.875rem'
            }}>
              Project Description
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            textAlign: 'left',
          }}>
            {defaultDescription}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}; 